import { todayISO, addDays, daysBetween } from "./utils";

// FSRS-6 default parameters (open-spaced-repetition/ts-fsrs packages/fsrs/src/constant.ts,
// default_w — 21 weights). w[20] is the trainable retrievability-decay parameter that
// replaces the old fixed DECAY = -0.5 used through FSRS-4.5/5.
export const FSRS_W = [0.212,1.2931,2.3065,8.2956,6.4133,0.8334,3.0194,0.001,1.8722,0.1666,0.796,1.4835,0.0614,0.2629,1.6483,0.6014,1.8729,0.5425,0.0912,0.0658,0.1542];
export const FSRS_DECAY = -FSRS_W[20];
export const FSRS_FACTOR = Math.pow(0.9, 1 / FSRS_DECAY) - 1;
const FSRS_S_MIN = 0.001;
const FSRS_S_MAX = 36500;

export function fsrsR(t, S) {
  if (!S || S <= 0 || t < 0) return 1;
  return Math.pow(1 + FSRS_FACTOR * t / S, FSRS_DECAY);
}
export function fsrsInitS(r) { return Math.max(FSRS_W[r - 1], 0.1); }
export function fsrsInitD(r) { return Math.min(10, Math.max(1, FSRS_W[4] - Math.exp(FSRS_W[5] * (r - 1)) + 1)); }
export function fsrsNextD(D, r) {
  const deltaD = -FSRS_W[6] * (r - 3);
  const damped = D + deltaD * (10 - D) / 9; // linear damping: less swing as D nears the ceiling
  const target = fsrsInitD(4); // mean-reverts toward D0(Easy), not a flat w4
  return Math.min(10, Math.max(1, FSRS_W[7] * target + (1 - FSRS_W[7]) * damped));
}
export function fsrsRecallS(D, S, R, r) {
  const hp = r === 2 ? FSRS_W[15] : 1, eb = r === 4 ? FSRS_W[16] : 1;
  const s = S * (1 + Math.exp(FSRS_W[8]) * (11 - D) * Math.pow(S, -FSRS_W[9]) * (Math.exp(FSRS_W[10] * (1 - R)) - 1) * hp * eb);
  return Math.min(FSRS_S_MAX, Math.max(FSRS_S_MIN, s));
}
export function fsrsForgetS(D, S, R) {
  const s = FSRS_W[11] * Math.pow(D, -FSRS_W[12]) * (Math.pow(S + 1, FSRS_W[13]) - 1) * Math.exp(FSRS_W[14] * (1 - R));
  return Math.min(FSRS_S_MAX, Math.max(FSRS_S_MIN, s));
}
export function fsrsReview(card, rating) {
  const today = todayISO();
  const t = card.lastReview ? daysBetween(card.lastReview, today) : 0;
  let S, D;
  if (!card.stability) {
    S = fsrsInitS(rating); D = fsrsInitD(rating);
  } else {
    D = fsrsNextD(card.difficulty || 5, rating);
    const R = fsrsR(t, card.stability);
    S = rating === 1 ? fsrsForgetS(card.difficulty || 5, card.stability, R)
                     : fsrsRecallS(card.difficulty || 5, card.stability, R, rating);
  }
  const interval = rating === 1 ? 1 : Math.max(1, Math.round(S));
  return {
    ...card, stability: S, difficulty: D, interval,
    due: addDays(today, interval), lastReview: today,
    reps: (card.reps || 0) + 1, lapses: (card.lapses || 0) + (rating === 1 ? 1 : 0),
    state: rating === 1 ? 'relearning' : 'review',
  };
}
export function makeFSRSCard(w) {
  return {
    id: w.id, turkish: w.turkish, english: w.english,
    example: w.example || null, example_translation: w.example_translation || null,
    topicOrder: w.topicOrder || null, topicName: w.topicName || null,
    topicId: w.topicId || null,
    state: 'new', stability: null, difficulty: null,
    due: todayISO(), lastReview: null, interval: 0, reps: 0, lapses: 0,
  };
}
export function makeD2State() {
  return { state: 'new', stability: null, difficulty: null, due: todayISO(), lastReview: null, interval: 0, reps: 0, lapses: 0 };
}
export function migrateSM2(card) {
  if (card.due !== undefined) return card;
  const today = todayISO();
  const interval = card.interval || 1;
  const due = (card.nextReview && card.nextReview > Date.now())
    ? new Date(card.nextReview).toISOString().slice(0, 10) : today;
  return {
    id: card.id, turkish: card.turkish || card.word || "",
    english: card.english || card.meaning || "",
    example: card.example || card.ex || null,
    example_translation: card.example_translation || card.exTr || null,
    topicOrder: null, topicName: null, state: 'review',
    stability: Math.max(1, interval), difficulty: 5,
    due, lastReview: today, interval, reps: card.repetitions || card.reps || 1, lapses: 0,
  };
}
export function cardMemState(card) {
  const iv = card.interval || 0;
  if (!card.stability || iv < 3)  return { key: 'inAcquisition', color: '#64748b' };
  if (iv < 21)  return { key: 'rooted',          color: '#10b981' };
  if (iv < 90)  return { key: 'fluent',           color: '#059669' };
  return          { key: 'deeplyRetained',        color: '#C9952A' };
}
